import React, { useState } from 'react';
import MetaTags from 'react-meta-tags';
import { Row, Col, Alert, Card, CardBody, Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';

import logo from '../../assets/images/small-logo.svg';
import LanguageDropdown from '../../components/Language-Dropdown';
import PasswordRulesList from './components/Password-Rules-List';
import DisplayErrors from 'components/Display-Errors';
import LabelGroup from 'components/Label-Group';
import TextInput from 'components/Text-Input';
import { SubmitButton } from './styles';

const ResetPassword = () => {
  const formConfig = {
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    shouldFocusError: true,
  };
  const form = useForm(formConfig);
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = form;
  const { t: translate } = useTranslation();
  const url = new URL(window.location.href);
  const code = url.searchParams.get('oobCode');
  const [alertColor, setAlertColor] = useState('danger');
  const [alertBool, setAlertBool] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const onSubmit = async (data) => {
    const password = data.password;

    const isValid = [
      password.length >= 8,
      password.length <= 25,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]/.test(password),
      password === data.confirmPassword,
    ].every(Boolean);

    if (!isValid) {
      setError('password', {
        type: 'manual',
        message: translate('Invalid Password!'),
      });
      return;
    }

    setAlertBool(false);
    const auth = getAuth();

    try {
      setIsSending(true);
      const isCode = await verifyPasswordResetCode(auth, code);
      if (isCode) {
        await confirmPasswordReset(auth, code, data.password);
        setAlertColor('success');
        setAlertMessage('Password changed successfully!');
        setAlertBool(true);
        setShowForm(false);
      }
    } catch (error) {
      setIsSending(false);
      setAlertColor('danger');
      setAlertMessage(error.message);
      setAlertBool(true);
      setShowForm(true);
    }
  };

  const handleErrors = (errors) => {
    if (!errors || Object.keys(errors).length === 0) {
      return {};
    }

    if (
      typeof errors === 'object' &&
      Object.values(errors).every((error) => typeof error === 'string')
    ) {
      return errors;
    }

    const formattedErrors = {};

    if (errors) {
      formattedErrors.required = errors.message || errors;
    }

    return formattedErrors;
  };

  const password = useWatch({ name: 'password', control: form.control });
  const confirmPassword = useWatch({ name: 'confirmPassword', control: form.control });

  const passwordRules = [
    {
      label: translate('Passwords match'),
      valid: password && confirmPassword && password === confirmPassword,
    },
    {
      label: translate('At least 8 characters'),
      valid: password?.length >= 8,
    },
    {
      label: translate('One uppercase letter'),
      valid: /[A-Z]/.test(password),
    },
    {
      label: translate('One lowercase letter'),
      valid: /[a-z]/.test(password),
    },
    {
      label: translate('One number'),
      valid: /\d/.test(password),
    },
    {
      label: translate('One special character'),
      valid: /[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]/.test(password),
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>{translate('Reset Password')} | Ectools</title>
      </MetaTags>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark">
          <i className="fas fa-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div style={{ backgroundColor: 'rgba(242, 242, 242, 0.22)' }}>
                  <Row>
                    <Col xs={9}>
                      <div className="text-primary p-4">
                        <h5 style={{ color: '#344792' }}>{translate('Reset Password')}</h5>
                        <p style={{ color: '#344792' }}>
                          {translate('Recover your access to the Dashboard.')}
                        </p>
                      </div>
                    </Col>
                    <Col className="col-3" style={{ paddingLeft: '8%' }}>
                      <LanguageDropdown />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div style={{ marginBottom: '2.5rem' }}>
                    <Link to="/">
                      <img
                        src={logo}
                        alt={translate("Ectools' Logo")}
                        height="36"
                        className="ms-2"
                      />
                    </Link>
                  </div>
                  <div className="p-2">
                    <Alert color={alertColor} isOpen={alertBool}>
                      {translate(alertMessage)}
                    </Alert>

                    {showForm ? (
                      <FormProvider {...form}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <LabelGroup
                            column
                            mb={2}
                            htmlFor="password"
                            label="Password"
                            required
                            size={12}
                          >
                            <TextInput
                              controlName="password"
                              type="password"
                              placeholder="Enter your new password"
                              required
                            />
                            <DisplayErrors error={handleErrors(errors?.password)} />
                          </LabelGroup>
                          <LabelGroup
                            column
                            mb={2}
                            htmlFor="confirmPassword"
                            label="Confirm the entered password"
                            required
                            size={12}
                          >
                            <TextInput
                              controlName="confirmPassword"
                              type="password"
                              placeholder="Confirm the entered password"
                              required
                              validation={{
                                validate: (value) =>
                                  value === form.getValues('password') ||
                                  translate('Passwords do not match'),
                              }}
                            />
                            <DisplayErrors error={handleErrors(errors?.confirmPassword)} />
                          </LabelGroup>
                          <PasswordRulesList rules={passwordRules} isPasswordEmpty={!password} />
                          <Row className="justify-content-end">
                            <Col lg="12">
                              <SubmitButton
                                type="submit"
                                className="col-lg-12"
                                disabled={isSending}
                              >
                                {isSending ? (
                                  <div className="spinner-border text-light" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  translate('Reset Password')
                                )}
                              </SubmitButton>
                            </Col>
                          </Row>
                        </form>
                      </FormProvider>
                    ) : null}

                    {!showForm ? (
                      <Row className="justify-content-end">
                        <Col lg="12">
                          <Link to="/login">
                            <SubmitButton type="submit" className="col-lg-12">
                              {translate('Log in')}
                            </SubmitButton>
                          </Link>
                        </Col>
                      </Row>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  © {new Date().getFullYear()} {translate('Ecto Digital')}.
                  {` ${translate('Created with')} `}
                  <i className="mdi mdi-heart text-danger" />
                  {` ${translate('in Curitiba')}`}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ResetPassword;
